// Lion Auto: what a Save group does once the group is in our database. With
// a floor (its own, the show's or the artist's) the saved group is priced
// like Calculate prices — the ladder is calculated and a complete answer is
// written to Skybox — and, for an admin, Auto goes on AFTER that write (the
// first runner pass then starts from the written prices and can't race the
// write's stale-price guard), and only when that write fully landed or
// every rank already matched. Without a floor it is saved only. Pricers get the pricing, never Auto (the
// server's AUTO_ADMIN_ONLY). A new group sent with nothing ranked by hand
// comes back ranked in Smart sort order (the server). The rules, the Auto
// box's starting state, the new group's Save hint and the toast the save
// ends with; pure, tested in lionAutoSave.test.js.
import { cleanFloorPct } from './lionAutoLadder';
import { effectiveFloor } from './lionAutoFloors';
import { retryAfterSec } from './lionAutoErrors';
import { writeOutcomeText } from './lionAutoWrite';

/**
 * The floor a saved group prices at: its own when > 0, else the show's or
 * the artist's (0 = none), or null while the inherited floor is unknown
 * (the floors not read yet), which is not "no floor".
 * @param {*} ownPct  criteria.ladderFloorPct
 * @param {{ pct, source }|null} inherited  inheritedFloorFor(floorsQ)
 * @returns {number|null}
 */
export function knownFloorPct(ownPct, inherited) {
  const own = cleanFloorPct(ownPct);
  if (own > 0) return own;
  if (inherited === null || inherited === undefined) return null;
  return effectiveFloor(ownPct, inherited).pct;
}

/**
 * The editor's Auto box when a saved group opens: ticked when the group is
 * on Auto; for an admin also ticked on a group that is off (Save turns Auto
 * on, and unticking it is how an admin keeps the group off), unless the
 * runner paused it: a paused group goes back on only when someone ticks it.
 */
export function autoSwitchDefault(group, adminLike) {
  if (!group) return false;
  if (group.autoEnabled) return true;
  return !!adminLike && group.autoLastStatus !== 'paused';
}

/**
 * The steps after the DB save.
 * @param {{ floorPct: number|null, rankedCount: number, adminLike: boolean, savedAuto: boolean,
 *   autoExplicitlyOff: boolean, paused: boolean }} input  floorPct: knownFloorPct; savedAuto: the
 *   group was on Auto before the save; autoExplicitlyOff: the Auto box ended unticked; paused: the
 *   runner paused the group and nobody ticked it
 * @returns {{ calc: boolean, autoOn: 'now'|'afterWrite'|null, wantsAuto: boolean,
 *   reason: 'NO_FLOOR'|'FLOORS_UNKNOWN'|null }}  calc: calculate and write the ladder;
 *   autoOn: turn Auto on right after the save (nothing ranked) or after the write attempt;
 *   wantsAuto: Auto would go on had the group a floor
 */
export function postSaveSteps({ floorPct, rankedCount, adminLike, savedAuto, autoExplicitlyOff, paused } = {}) {
  const wantsAuto = !!adminLike && !savedAuto && !autoExplicitlyOff && !paused;
  if (floorPct === null || floorPct === undefined) return { calc: false, autoOn: null, wantsAuto, reason: 'FLOORS_UNKNOWN' };
  if (!(Number(floorPct) > 0)) return { calc: false, autoOn: null, wantsAuto, reason: 'NO_FLOOR' };
  const ranked = Number(rankedCount) > 0;
  let autoOn = null;
  if (wantsAuto) autoOn = ranked ? 'afterWrite' : 'now';
  return { calc: ranked, autoOn, wantsAuto, reason: null };
}

/**
 * Whether Auto goes on now: at once when nothing was ranked; else only when
 * the pricing step wrote with status done (the server answered every row),
 * found every rank already at its price, or found no comp with the no-comp
 * step-down on (stepDown: nothing written, the runner lowers the prices from
 * the current ones on its own clock). Never after a write that
 * stopped, was rate limited, timed out or never reported back, nor when the
 * ladder did not finish or pricing never started: the prices are not what
 * the ladder says, and the runner would start from them.
 * @param {object|null} steps  postSaveSteps
 * @param {{ action: string, result?: object|null }|null} priced  how the pricing step ended
 */
export function shouldTurnAutoOn(steps, priced) {
  if (steps?.autoOn === 'now') return true;
  if (steps?.autoOn !== 'afterWrite') return false;
  if (priced?.action === 'unchanged' || priced?.action === 'stepDown') return true;
  return priced?.action === 'write' && priced.result?.status === 'done';
}

/** A failed Auto-on (PUT { autoEnabled: true }) in words, for the toast */
export function autoOnErrorText(err) {
  const status = err?.response?.status;
  const data = err?.response?.data && typeof err.response.data === 'object' ? err.response.data : {};
  if (data.code === 'AUTO_ADMIN_ONLY') return 'only an admin can turn Auto on';
  if (data.code === 'AUTO_NEEDS_FLOOR') return 'it needs a floor (group, show or artist)';
  if (status === 429) return `too many requests: tick Auto in the editor in ${retryAfterSec(err)} s`;
  if (status === 404) return 'the group no longer exists';
  if (status === 403) return 'you do not have access to this event';
  if (!status) return 'the server did not answer: open the group to check';
  return data.error || 'turning it on failed';
}

// What the pricing step did: { bad, text }
function pricedText(priced) {
  if (priced?.action === 'unchanged') return { bad: false, text: 'prices already match the ladder' };
  if (priced?.action === 'stepDown') return { bad: false, text: 'no comp, so nothing written: on Auto the prices step down toward the floor' };
  if (priced?.action === 'blocked') return { bad: true, text: `not priced: ${priced.reason}` };
  if (priced?.action !== 'write') return { bad: true, text: 'nothing written: the ladder did not finish (see the price ladder)' };
  return writeOutcomeText(priced.result);
}

// Why Auto stayed off after the pricing step (shouldTurnAutoOn said no), or null
function autoOffText(priced) {
  if (priced?.action === 'blocked') return 'Auto stays off: nothing was priced';
  if (priced?.action === 'incomplete') return 'Auto stays off: the ladder did not finish';
  if (priced?.action !== 'write') return null;
  if (!priced.result) return 'Auto stays off: the write did not report back';
  const { status } = priced.result;
  if (status === 'unknown') return 'Auto stays off until the prices show';
  if (status === 'failed' || status === 'rate_limited') return 'Auto stays off: the write did not finish';
  return null;
}

/**
 * The toast a Save group ends with: "Saved “X” — wrote 4 prices — Auto is
 * on", every failure named (kind 'error'). A new group the server ranked
 * says so right after the name: "Saved “X” — ranked 4 listings in Smart sort
 * order — wrote 4 prices".
 * @param {{ name: string, steps: object, priced?: { action: 'write'|'unchanged'|'incomplete'|'stepDown'|'blocked',
 *   result?: object|null, reason?: string }|null, auto?: { ok: boolean, error?: string }|null, autoOff?: boolean,
 *   smartRanked?: number }} input
 *   priced: how the pricing step ended (null when there was none; stepDown: no comp, the no-comp
 *   step-down priced the ladder and nothing was written); auto: the Auto-on call, null when
 *   not made; autoOff: the save itself turned Auto off; smartRanked: how many listings the
 *   server ranked in Smart sort order (a new group sent with no ranks), 0 or absent for none
 * @returns {{ kind: 'success'|'error', text: string }}
 */
export function postSaveText({ name, steps, priced = null, auto = null, autoOff = false, smartRanked = 0 }) {
  const s = steps || {};
  const parts = [`Saved “${name}”`];
  let bad = false;
  const ranked = Number(smartRanked);
  if (Number.isInteger(ranked) && ranked > 0) {
    parts.push(`ranked ${ranked} listing${ranked === 1 ? '' : 's'} in Smart sort order`);
  }
  const autoStaysOff = s.wantsAuto ? ' and Auto stays off' : '';
  if (s.reason === 'NO_FLOOR') {
    parts.push(`no floor set, so nothing was priced${autoStaysOff}`);
  } else if (s.reason === 'FLOORS_UNKNOWN') {
    bad = true;
    parts.push(`the floors were not read yet, so nothing was priced${autoStaysOff}: Calculate prices once they show`);
  } else if (!s.calc) {
    parts.push('nothing ranked, so nothing was priced');
  } else {
    const p = pricedText(priced);
    bad = bad || p.bad;
    parts.push(p.text);
  }
  if (auto?.ok) {
    parts.push('Auto is on');
  } else if (auto) {
    bad = true;
    parts.push(`Auto is not on: ${auto.error}`);
  } else if (s.autoOn === 'afterWrite' && autoOffText(priced)) {
    parts.push(autoOffText(priced));
  } else if (autoOff) {
    parts.push('Auto is off');
  }
  return { kind: bad ? 'error' : 'success', text: parts.join(' — ') };
}

/**
 * How many listings the server ranked in Smart sort order on a save (the
 * toast's smartRanked): a new group (no savedId) sent with no ranks comes
 * back ranked, its members up to 500. 0 for an edit, a group ranked by hand
 * (saved as sent), or one with none of our listings.
 * @param {{ savedId?: number|null, sentIds?: Array, savedIds?: Array }} [input]
 *   savedId: the group's id before the save (null = new); sentIds: the
 *   ladderIds sent; savedIds: the ladderIds the server's copy came back with
 * @returns {number}
 */
export function smartRankedCount({ savedId = null, sentIds, savedIds } = {}) {
  if (savedId != null) return 0;
  if (Array.isArray(sentIds) && sentIds.length > 0) return 0;
  return Array.isArray(savedIds) ? savedIds.length : 0;
}

// Smart sort in words, for the hint's tooltip
const SMART_SORT_WORDS = 'qty 1 first, then 2-3, then 4+, worst rows first in each; rank 1 = the lowest price';

/**
 * The new group editor's line on what Save does, for every user. Nothing
 * ranked by hand: the server ranks the members in Smart sort order (worst
 * rows first); ranked by hand: saved in that order. Then, with a floor, the
 * prices are written; for an admin (canEnableAuto) Auto goes on after.
 * @param {{ ladderIds?: Array, canEnableAuto?: boolean }} [input]  ladderIds: the draft's ranking
 * @returns {{ text: string, title: string }}
 */
export function newGroupSaveHint({ ladderIds, canEnableAuto = false } = {}) {
  const handRanked = Array.isArray(ladderIds) && ladderIds.length > 0;
  const ranks = handRanked ? 'Saving keeps your ranks' : 'Saving ranks your listings in Smart sort order';
  const title = handRanked
    ? 'You ranked listings in the table, so the group is saved in your order.'
    : `Nothing is ranked by hand, so the server ranks the group's listings in Smart sort order (${SMART_SORT_WORDS}). `
      + 'Rank them in the table to save your own order.';
  const text = canEnableAuto
    ? `${ranks}, prices the group and turns Auto on (needs a floor).`
    : `${ranks} and, with a floor, writes their prices.`;
  return { text, title };
}
